import isObject from 'lodash/isObject';
import isString from 'lodash/isString';

/**
 * Build URLSearchParams from a flat params object using the native API.
 * Arrays become repeated keys (`a=1&a=2`); `undefined` is omitted; `null` becomes `key=`.
 * Replaces qs.stringify — see PBI 173831.
 */
export function buildSearchParams(params: Record<string, unknown>): URLSearchParams {
    const searchParams = new URLSearchParams();

    const append = (key: string, value: unknown) => {
        if (value !== undefined) {
            searchParams.append(key, value === null ? '' : String(value));
        }
    };

    for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
            value.forEach(item => append(key, item));
        } else {
            append(key, value);
        }
    }

    return searchParams;
}

/** Serialize a flat params object to a query string (see {@link buildSearchParams}). */
export function buildQueryString(params: Record<string, unknown>): string {
    return buildSearchParams(params).toString();
}

enum ContentType {
    JSON = 'application/json',
    FORM = 'application/x-www-form-urlencoded',
    MULTIPART = 'multipart/form-data',
    TEXT = 'text/plain',
}

type IData = FormData | URLSearchParams | object | string | null;

export const getContentType = (data: unknown, contentType?: string) => {
    // If content type is specified, use it as is. Otherwise, try to guess it.
    if (contentType) {
        return contentType;
    }

    if (data instanceof FormData) {
        return ContentType.MULTIPART;
    }

    // Default form data. So if application/json is not specified, we assume that
    // form data is expected.
    if (isObject(data)) {
        return ContentType.FORM;
    }

    // This is default content type for fetch.
    return ContentType.TEXT;
};

export const isValidData = (data: unknown): data is IData => {
    return data === null || isObject(data) || isString(data);
};

export const serializeData = (data: IData, contentType: string) => {
    if (contentType.includes(ContentType.MULTIPART) && data instanceof FormData) {
        return data;
    }

    if (contentType.includes(ContentType.FORM)) {
        if (data instanceof URLSearchParams) {
            return data;
        }

        if (!isObject(data)) {
            return null;
        }

        const searchParams = buildSearchParams(data as Record<string, unknown>);
        return searchParams.keys().next().done ? null : searchParams;
    }

    if (contentType.includes(ContentType.JSON)) {
        return isObject(data) ? JSON.stringify(data) : data;
    }

    if (contentType.includes(ContentType.TEXT) && isString(data)) {
        return data;
    }

    return null;
};
